import React, { FC, HTMLAttributes, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { PersonTypes } from '@frontend/api-person';
import { useCallInsightController } from '@frontend/call-insight-panel';
import {
  callStatusUtils,
  generateTrackingId,
  PrivateField,
  useCallIntelDefaultsShallowStore,
  CI_DATE_FORMATS,
} from '@frontend/call-intel-core';
import { useComponentEngagementShallowStore } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Heading, SkeletonLoader, Text, TextLink } from '@frontend/design-system';
import { usePatientInsightsRequests } from '../hooks/use-patient-insights-requests.js';
import {
  CallInsightFilter,
  PatientCallTrendsTabType,
  PersonInfoStoreProvider,
  useCallIntelPatientInsights,
  usePersonInfoContext,
} from '../providers.tsx';
import { CategoryChart } from './charts/category-chart';
import { SentimentChart } from './charts/sentiment-chart';
import IntelCard from './intel-card.js';
import PatientCallTrends from './patient-call-trends';

export interface PatientInsightsActions {
  /** when the “primary” header button is clicked (e.g. “View History”) */
  onPrimaryActionClick?: () => void;
  /** when the user clicks on a chart slice/bar to apply a filter */
  onChartClick?: () => void;
  /** when you want to apply or clear a filter */
  onFilterChange?: (filter: CallInsightFilter) => void;
}

interface BasePatientInsightProps {
  title?: string;
  primaryActionLabel?: string;
  actions?: PatientInsightsActions;
  trackComponentInteraction?: {
    componentId: string;
    enable: boolean;
  };
}

interface PatientInsightsProps extends BasePatientInsightProps {
  person?: PersonTypes.Person;
}

interface PatientInsightsComponentProps extends BasePatientInsightProps, HTMLAttributes<HTMLDivElement> {}

interface InsightsHeaderProps extends BasePatientInsightProps {
  showAction: boolean;
}

const TRACKING_BASE = {
  component: 'patient-insights',
  page: 'contact-panel',
};

const InsightsLoader = () => {
  return (
    <div css={styles.loaderWrapper}>
      <SkeletonLoader height={136} />
      <SkeletonLoader height={20} />
    </div>
  );
};

const InsightsHeader = ({ actions = {}, title, showAction, primaryActionLabel }: InsightsHeaderProps) => {
  const { t } = useTranslation('callIntelInsights');
  const { onPrimaryActionClick } = actions;

  const showCta = showAction && onPrimaryActionClick;

  return (
    <div css={styles.actionWrapper}>
      <Heading level={3}>{title ?? t('Call Intelligence')}</Heading>
      {showCta && (
        <TextLink
          size='medium'
          weight='bold'
          onClick={onPrimaryActionClick}
          trackingId={generateTrackingId({
            ...TRACKING_BASE,
            context: 'primary-btn-click',
          })}
        >
          {primaryActionLabel ?? t('View History')}
        </TextLink>
      )}
    </div>
  );
};

const PatientInsightsComponent: FC<PatientInsightsComponentProps> = ({
  actions = {},
  trackComponentInteraction,
  ...rest
}) => {
  const { t } = useTranslation('callIntelInsights');
  const { patientCapitalized: patientCapitalizedToken } = useTranslationTokens();
  const { personInfo } = usePersonInfoContext();
  const { selectedLocationIds } = useAppScopeStore();
  const selectedLocationId = selectedLocationIds?.[0] || '';

  const { activeTab, setActiveTab, showTrends, setShowTrends, dateRange } = useCallIntelPatientInsights();
  const { lastCallDetail, isLastCallLoading, callTrends, isCallTrendsLoading } = usePatientInsightsRequests({
    dateRange,
    personId: personInfo.PersonID,
    showTrends,
  });

  const { trackInteraction } = useComponentEngagementShallowStore('trackInteraction');

  const { open: openCallInsightPanel, setIsDemoAccount } = useCallInsightController({
    source: 'patientInsightsContactPanel',
  });
  const { dataLabels } = useCallIntelDefaultsShallowStore('dataLabels');
  const { onChartClick, onFilterChange } = actions;

  const { call, summary } = lastCallDetail ?? {};

  const isEmpty = !call;

  const { isSkippedCall, isProcessingCall, isAnalyzed, isPrivateCall } = callStatusUtils.getCallFlags(call);

  const isTrendsDataLimited = false; /* true if < 1 year of CI data */ // TODO: Replace with actual value from api

  const registerInteraction = useCallback(() => {
    if (trackComponentInteraction?.enable) {
      trackInteraction({
        componentId: trackComponentInteraction.componentId,
      });
    }
  }, [trackInteraction, trackComponentInteraction]);

  const handleChartClick = useCallback(
    (filter: CallInsightFilter) => {
      onFilterChange?.(filter);
      onChartClick?.();
      registerInteraction();
    },
    [onFilterChange, onChartClick, registerInteraction]
  );

  const handleOpenCallTakeaway = () => {
    if (call) {
      const isDemo = personInfo.PersonID.startsWith('demo');
      if (isDemo) {
        call.locationId ||= selectedLocationId; // TODO: Remove this selectedLocationId fallback when BE fixes the issue
        call.person ||= {
          id: personInfo.PersonID,
          firstName: personInfo.FirstName,
          lastName: personInfo.LastName,
        };
      }
      setIsDemoAccount(isDemo);
      openCallInsightPanel({
        channelId: call.id ?? '',
        locationId: call.locationId ?? '',
        includeCallIntelligence: false,
        callIntelData: call,
      });
      registerInteraction();
    }
  };

  const renderCallContent = useMemo(() => {
    if (isProcessingCall) {
      return <IntelCard.ProcessingCall />;
    }

    if (!isAnalyzed && !isPrivateCall) {
      return <IntelCard.NotAnalyzed />;
    }

    if (isPrivateCall) {
      return <PrivateField isCompact={false} />;
    }

    return (
      <>
        <IntelCard.Attributes>
          <IntelCard.Sentiment sentiment={call?.callSentiment?.sentiment} />
          <IntelCard.Category category={call?.categories?.[0]} />
          <IntelCard.Tasks count={call?.taskCount} />
        </IntelCard.Attributes>
        <IntelCard.Summary>{summary}</IntelCard.Summary>
      </>
    );
  }, [
    call?.callSentiment?.sentiment,
    call?.categories,
    call?.taskCount,
    isAnalyzed,
    isPrivateCall,
    isProcessingCall,
    summary,
  ]);

  const formattedDate = useMemo(() => {
    return call?.startTime ? dayjs(call.startTime).format(CI_DATE_FORMATS.DATE) : '';
  }, [call?.startTime]);

  return (
    <div css={[styles.containerStyles({ isLoading: isLastCallLoading })]} {...rest} data-testid='patient-insights'>
      <InsightsHeader actions={actions} showAction={!isEmpty && !isLastCallLoading} {...rest} />

      {isLastCallLoading && <InsightsLoader />}

      {!isLastCallLoading && isEmpty && <IntelCard.EmptyState />}

      {!isLastCallLoading && !isEmpty && (
        <>
          <IntelCard
            onClick={handleOpenCallTakeaway}
            trackingId={generateTrackingId({
              ...TRACKING_BASE,
              context: 'last-call-card-click',
            })}
          >
            <IntelCard.Header
              title={t('Last Call')}
              isAnalyzed={isAnalyzed}
              isProcessingCall={isProcessingCall}
              leftIcon={
                <Icon
                  name={call?.direction === 'DIRECTION_OUTBOUND' ? 'phone-outgoing-small' : 'phone-incoming-small'}
                />
              }
              rightIcon={
                isPrivateCall ? undefined : !isSkippedCall ? (
                  <Icon name='error-badge-small' color='error' />
                ) : (
                  <Icon name='help-filled-small' primaryColor='disabled' secondaryColor='subdued' />
                )
              }
              value={formattedDate}
              tooltipContent={
                isSkippedCall
                  ? t(
                      'Calls are not analyzed if they have a single participant, very short audio, or audio exceeding an hour in length.'
                    )
                  : t('We are unable to transcribe and analyze this call. ')
              }
            />
            {renderCallContent}
          </IntelCard>
          <PatientCallTrends<PatientCallTrendsTabType>
            activeTab={activeTab}
            defaultTab='sentiment-tab'
            isLoading={isCallTrendsLoading}
            isOpen={showTrends}
            onTabChange={setActiveTab}
            onToggle={(open) => {
              setShowTrends(open);
              registerInteraction();
            }}
          >
            <PatientCallTrends.Header
              duration={t('Last 12 Months')}
              isLimitedData={isTrendsDataLimited}
              title={t('{{patientCapitalizedToken}} Call Trends', { patientCapitalizedToken })}
              tooltipContent={t(
                '{{patientCapitalizedToken}} Call Trends are limited due to this location having less than a year of Call Intelligence data.',
                { patientCapitalizedToken }
              )}
              trackingId={generateTrackingId({
                ...TRACKING_BASE,
                context: 'call-trends-accordion-click',
              })}
            />

            <PatientCallTrends.Content>
              <PatientCallTrends.Navigation fullWidth>
                <PatientCallTrends.Item
                  id='sentiment-tab'
                  trackingId={generateTrackingId({
                    ...TRACKING_BASE,
                    context: 'call-trends-sentiment-tab',
                  })}
                >
                  {t('Customer Sentiment')}
                </PatientCallTrends.Item>
                <PatientCallTrends.Item
                  id='category-tab'
                  trackingId={generateTrackingId({
                    ...TRACKING_BASE,
                    context: 'call-trends-categories-tab',
                  })}
                >
                  {t('Call Categories')}
                </PatientCallTrends.Item>
              </PatientCallTrends.Navigation>

              <PatientCallTrends.View controller='sentiment-tab'>
                <SentimentChart
                  sentiments={callTrends?.sentiments ?? {}}
                  isLoading={isCallTrendsLoading}
                  onSliceClick={({ name }) =>
                    handleChartClick({ filterType: 'sentiment', key: name, label: dataLabels.sentiments?.[name] ?? '' })
                  }
                />
              </PatientCallTrends.View>
              <PatientCallTrends.View controller='category-tab'>
                <CategoryChart
                  categories={callTrends?.categories ?? {}}
                  isLoading={isCallTrendsLoading}
                  onBarClick={({ barId }) =>
                    handleChartClick({
                      filterType: 'category',
                      key: barId,
                      label: dataLabels.categories?.[barId] ?? '',
                    })
                  }
                />
              </PatientCallTrends.View>
            </PatientCallTrends.Content>
          </PatientCallTrends>

          {!isLastCallLoading && (
            <Text color='subdued' css={styles.footer} size='small'>
              {t('This AI-generated data may not always be accurate. Please review the Call Insights carefully.')}
            </Text>
          )}
        </>
      )}
    </div>
  );
};

export const PatientInsights = React.memo(({ person, ...rest }: PatientInsightsProps) => {
  if (!person) return null;

  return (
    <PersonInfoStoreProvider personInfo={person}>
      <PatientInsightsComponent {...rest} />
    </PersonInfoStoreProvider>
  );
});

PatientInsights.displayName = 'PatientInsights';

const styles = {
  containerStyles: ({ isLoading }: { isLoading: boolean }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(2)};
    gap: ${theme.spacing(2)};
    border-radius: ${theme.borderRadius.medium};
    background-color: ${theme.colors.white};
    min-height: ${isLoading ? 250 : 0}px;
    max-height: ${isLoading ? 250 : 600}px;
    height: auto;
    transition: max-height 0.5s ease-in-out, min-height 0.5s ease-in-out;
    overflow: hidden;
  `,
  actionWrapper: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  footer: css`
    font-style: italic;
    border-top: 1px solid ${theme.colors.neutral20};
    padding-top: ${theme.spacing(1)};
  `,
  loaderWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
};
